import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js';

// Types for web Stripe context
interface StripeContextType {
  stripe: Stripe | null;
  isLoading: boolean;
  error: string | null;
  initPaymentSheet: (params: InitPaymentSheetParams) => Promise<{ error?: { message: string } }>;
  presentPaymentSheet: () => Promise<{ error?: { code: string; message: string } }>;
  isPlatformPaySupported: () => Promise<boolean>;
  confirmPayment: (clientSecret: string) => Promise<{ error?: { message: string } }>;
}

interface InitPaymentSheetParams {
  merchantDisplayName?: string;
  paymentIntentClientSecret?: string;
  setupIntentClientSecret?: string;
  returnURL?: string;
  allowsDelayedPaymentMethods?: boolean;
  style?: string;
  defaultBillingDetails?: {
    name?: string;
    email?: string;
  };
  applePay?: { merchantCountryCode: string };
  googlePay?: { merchantCountryCode: string; testEnv?: boolean };
  appearance?: { colors?: { primary?: string } };
}

const StripeContext = createContext<StripeContextType | null>(null);

interface StripeProviderWebProps {
  publishableKey: string;
  merchantIdentifier?: string;
  urlScheme?: string;
  children: ReactNode;
}

// Store for current payment session
let currentClientSecret: string | null = null;
let currentPaymentConfig: InitPaymentSheetParams | null = null;
let isSetupIntent: boolean = false;

export function StripeProviderWeb({ 
  publishableKey, 
  merchantIdentifier, 
  urlScheme, 
  children 
}: StripeProviderWebProps) {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!publishableKey) {
      setError('Stripe publishable key is required');
      setIsLoading(false);
      return;
    }

    loadStripe(publishableKey)
      .then((stripeInstance) => {
        setStripe(stripeInstance);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('[StripeWeb] Failed to load Stripe:', err);
        setError(err.message || 'Failed to load Stripe');
        setIsLoading(false);
      });
  }, [publishableKey]);

  const initPaymentSheet = useCallback(async (params: InitPaymentSheetParams) => {
    if (!stripe) {
      return { error: { message: 'Stripe not initialized' } };
    }

    // Store the config for when we present the payment sheet
    // Track whether this is a setup intent (saving card) or payment intent (paying now)
    isSetupIntent = !!params.setupIntentClientSecret;
    currentClientSecret = params.setupIntentClientSecret || params.paymentIntentClientSecret || null;
    currentPaymentConfig = params;
    
    console.log('[StripeWeb] initPaymentSheet:', { isSetupIntent, hasClientSecret: !!currentClientSecret });
    
    return { error: undefined };
  }, [stripe]);

  const presentPaymentSheet = useCallback(async () => {
    if (!stripe || !currentClientSecret) {
      return { error: { code: 'NotInitialized', message: 'Payment sheet not initialized' } };
    }

    try {
      // On web, we'll use Stripe Checkout or Elements
      // For now, redirect to Stripe Checkout
      const elements = stripe.elements({
        clientSecret: currentClientSecret,
        appearance: {
          theme: 'stripe',
        },
      });

      // Create payment element
      const paymentElement = elements.create('payment');
      
      // Create a modal/overlay for the payment form
      const overlay = document.createElement('div');
      overlay.id = 'stripe-payment-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
      `;

      const modal = document.createElement('div');
      modal.style.cssText = `
        background: white;
        padding: 24px;
        border-radius: 12px;
        max-width: 500px;
        width: 90%;
        max-height: 90vh;
        overflow-y: auto;
      `;

      const title = document.createElement('h2');
      // Different title based on whether we're saving a card or making a payment
      title.textContent = isSetupIntent 
        ? 'Add Payment Method' 
        : (currentPaymentConfig?.merchantDisplayName || 'Complete Payment');
      title.style.cssText = 'margin: 0 0 16px 0; font-size: 20px; color: #333;';

      const formContainer = document.createElement('div');
      formContainer.id = 'stripe-payment-element';
      formContainer.style.cssText = 'margin-bottom: 16px;';

      const buttonContainer = document.createElement('div');
      buttonContainer.style.cssText = 'display: flex; gap: 12px; justify-content: flex-end;';

      const cancelButton = document.createElement('button');
      cancelButton.textContent = 'Cancel';
      cancelButton.style.cssText = `
        padding: 12px 24px;
        border: 1px solid #ddd;
        border-radius: 8px;
        background: white;
        cursor: pointer;
        font-size: 16px;
      `;

      const payButton = document.createElement('button');
      // Different button text based on whether we're saving a card or making a payment
      payButton.textContent = isSetupIntent ? 'Save Card' : 'Pay Now';
      payButton.style.cssText = `
        padding: 12px 24px;
        border: none;
        border-radius: 8px;
        background: #5469d4;
        color: white;
        cursor: pointer;
        font-size: 16px;
        font-weight: 600;
      `;

      buttonContainer.appendChild(cancelButton);
      buttonContainer.appendChild(payButton);

      modal.appendChild(title);
      modal.appendChild(formContainer);
      modal.appendChild(buttonContainer);
      overlay.appendChild(modal);
      document.body.appendChild(overlay);

      // Mount the payment element
      paymentElement.mount('#stripe-payment-element');

      return new Promise<{ error?: { code: string; message: string } }>((resolve) => {
        // Flag to prevent accidental immediate clicks during modal setup
        let canClose = false;
        setTimeout(() => { canClose = true; }, 300);
        
        cancelButton.onclick = () => {
          if (!canClose) return;
          paymentElement.destroy();
          overlay.remove();
          resolve({ error: { code: 'Canceled', message: 'The payment was canceled by the user' } });
        };

        overlay.onclick = (e) => {
          if (!canClose) return;
          if (e.target === overlay) {
            paymentElement.destroy();
            overlay.remove();
            resolve({ error: { code: 'Canceled', message: 'The payment was canceled by the user' } });
          }
        };

        payButton.onclick = async () => {
          payButton.disabled = true;
          payButton.textContent = 'Processing...';

          let confirmResult;
          
          // On web, we must use a valid web URL for return_url
          // Native deep links like "com.jadetrade://" don't work on web
          const configuredReturnUrl = currentPaymentConfig?.returnURL;
          const isNativeDeepLink = configuredReturnUrl && 
            (configuredReturnUrl.includes('://') && !configuredReturnUrl.startsWith('http'));
          const webReturnUrl = isNativeDeepLink ? window.location.href : (configuredReturnUrl || window.location.href);
          
          if (isSetupIntent) {
            // For setup intents (saving a card), use confirmSetup
            confirmResult = await stripe.confirmSetup({
              elements,
              confirmParams: {
                return_url: webReturnUrl,
              },
              redirect: 'if_required',
            });
          } else {
            // For payment intents, use confirmPayment
            confirmResult = await stripe.confirmPayment({
              elements,
              confirmParams: {
                return_url: webReturnUrl,
              },
              redirect: 'if_required',
            });
          }

          const { error: confirmError } = confirmResult;

          if (confirmError) {
            payButton.disabled = false;
            payButton.textContent = isSetupIntent ? 'Save Card' : 'Pay Now';
            
            if (confirmError.type === 'card_error' || confirmError.type === 'validation_error') {
              // Show error in modal instead of closing
              const errorDiv = document.createElement('div');
              errorDiv.style.cssText = 'color: #dc3545; margin-bottom: 16px; padding: 12px; background: #fff3f3; border-radius: 8px;';
              errorDiv.textContent = confirmError.message || (isSetupIntent ? 'Failed to save card' : 'Payment failed');
              formContainer.insertBefore(errorDiv, formContainer.firstChild);
            } else {
              paymentElement.destroy();
              overlay.remove();
              resolve({ error: { code: confirmError.code || 'unknown', message: confirmError.message || (isSetupIntent ? 'Failed to save card' : 'Payment failed') } });
            }
          } else {
            paymentElement.destroy();
            overlay.remove();
            resolve({ error: undefined });
          }
        };
      });
    } catch (err: any) {
      return { error: { code: 'Error', message: err.message || 'Payment failed' } };
    }
  }, [stripe]);

  const isPlatformPaySupported = useCallback(async () => {
    if (!stripe) return false;
    
    try {
      const paymentRequest = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Test',
          amount: 100,
        },
      });
      
      const result = await paymentRequest.canMakePayment();
      return !!result;
    } catch {
      return false;
    }
  }, [stripe]);

  const confirmPayment = useCallback(async (clientSecret: string) => {
    if (!stripe) {
      return { error: { message: 'Stripe not initialized' } };
    }

    try {
      const { error } = await stripe.confirmPayment({
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        return { error: { message: error.message || 'Payment confirmation failed' } };
      }

      return { error: undefined };
    } catch (err: any) {
      return { error: { message: err.message || 'Payment confirmation failed' } };
    }
  }, [stripe]);

  const value: StripeContextType = {
    stripe,
    isLoading,
    error,
    initPaymentSheet,
    presentPaymentSheet,
    isPlatformPaySupported,
    confirmPayment,
  };

  return (
    <StripeContext.Provider value={value}>
      {children}
    </StripeContext.Provider>
  );
}

export function useStripeWeb() {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error('useStripeWeb must be used within StripeProviderWeb');
  }
  return context;
}

// Export a hook that mimics the native Stripe hook interface
export function useStripe() {
  const context = useContext(StripeContext);
  
  if (!context) {
    // Return stub functions when not in provider (shouldn't happen in normal use)
    return {
      initPaymentSheet: async () => ({ error: { message: 'Stripe not initialized' } }),
      presentPaymentSheet: async () => ({ error: { code: 'NotInitialized', message: 'Stripe not initialized' } }),
      isPlatformPaySupported: async () => false,
      confirmPayment: async () => ({ error: { message: 'Stripe not initialized' } }),
    };
  }
  
  return {
    initPaymentSheet: context.initPaymentSheet,
    presentPaymentSheet: context.presentPaymentSheet,
    isPlatformPaySupported: context.isPlatformPaySupported,
    confirmPayment: context.confirmPayment,
  };
}

export default StripeProviderWeb;

